import { DateStep, fileDownload, useAppSelector } from '@frontend/common';
import { CommonMessage, useLocale } from '@frontend/lang';
import { useModals } from '@frontend/modal';
import { JobClient } from '@frontend/report/api';
import { useAccountRepository } from '@frontend/repository';
import { ToastUtil } from '@frontend/toast-utils';
import { UserClient } from '@frontend/user/api';
import { userStore } from '@frontend/user/redux';
import { IUser } from '@frontend/user/types';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { ExportModalProps } from './export-modal.component';

export const useExportModal = (props: ExportModalProps) => {
    const { closeModal } = useModals();
    const { locale } = useLocale();
    const { currentAccount } = useAccountRepository();
    const userState = useAppSelector(useSelector, userStore);
    const currentUser = userState.current;
    const [receivers, setReceivers] = useState<string[]>();
    const [user, setUser] = useState<IUser | null>(null);

    useEffect(() => {
        if (!currentUser) return;
        UserClient.resolveUsers([currentUser.id]).then((res) => {
            if (res && res.results.length > 0) {
                setReceivers([res.results[0].id]);
                setUser(res.results[0]);
            }
        });
    }, [currentUser]);

    const extraData = useMemo(() => {
        if (!props.data) return {};
        return convertData(props.data, props.searchKeys, props.dataKeys);
    }, [props.data, props.searchKeys, props.dataKeys]);

    async function handleExport() {
        const job = await JobClient.postJob(props.report.account_id, props.report.id, {
            receivers: receivers,
            language: locale,
            period: DateStep.CUSTOM,
            ...extraData
        });
        if (!job) {
            ToastUtil.error(
                CommonMessage.ERRORS.DEFAULT,
                <FormattedMessage
                    id='export-modal.export-failed'
                    defaultMessage='Export failed'
                />
            );
            return;
        }
        ToastUtil.success(
            CommonMessage.TOAST.PROMISE.SUCCESS,
            <FormattedMessage
                id='export-modal.export-started'
                defaultMessage='Export started'
            />
        );
        closeModal(props.modalId);
    }

    async function handleDownloadExport() {
        const blob = await JobClient.exportJob(props.report.account_id, props.report.id, {
            receivers: receivers,
            language: locale,
            period: DateStep.CUSTOM,
            ...extraData
        });
        if (!blob) {
            ToastUtil.error(
                CommonMessage.ERRORS.DEFAULT,
                <FormattedMessage
                    id='export-modal.export-failed'
                    defaultMessage='Export failed'
                />
            );
            return;
        }
        const formattedDate = new Date().toISOString().slice(0, 19).replace('T', '_').replace(/:/g, '-');
        fileDownload(blob, `${props.report.name || 'export'}-${formattedDate}.xlsx`);
        closeModal(props.modalId);
    }

    return {
        receivers,
        setReceivers,
        handleExport,
        closeModal,
        extraData,
        handleDownloadExport,
        currentUser,
        currentAccount,
        user
    };
};

const convertData = (data: { [key: string]: any }, searchKeys?: string[], dataKeys?: string[]) => {
    const obj: Record<string, any> = {};
    for (const key in data) {
        if (key === 'before') {
            obj['period_to_timestamp'] = data[key];
        } else if (key === 'after') {
            obj['period_from_timestamp'] = data[key];
        } else if (key === 'below_minimum') {
            obj['data'] = { ...(obj['data'] || {}), below_minimum: data[key] ? '1' : '0' };
        } else if (searchKeys && searchKeys.includes(key)) {
            obj['data'] = { ...(obj['data'] || {}), [key]: data[key] };
        } else if (dataKeys && dataKeys.includes(key)) {
            obj['data'] = { ...(obj['data'] || {}), [key]: data[key] };
        } else if (!IGNORED_EXPORT_DATA_KEYS.has(key)) {
            obj['data'] = { ...(obj['data'] || {}), [key]: data[key] };
        }
    }
    return obj;
};

const IGNORED_EXPORT_DATA_KEYS = new Set(['account_id', 'index', 'language', 'size', 'sort_direction', 'sort_field', 'tab']);
